import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import CanonicalTag from "@/components/CanonicalTag";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { ChevronLeft, ChevronRight, Calculator, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import { NeumorphicThemeProvider, useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";
import NeumorphicThemeToggle from "@/components/NeumorphicThemeToggle";
import { NeumorphicContainer, NeumorphicCard } from "@/components/neumorphic";

// Declare Calendly types for TypeScript
declare global {
  interface Window {
    Calendly: {
      initPopupWidget: (options: { url: string }) => void;
    };
  }
}

interface FormData {
  aduType: string;
  county: string;
  city: string;
  purposes: string[];
  otherPurpose: string;
  designChoice: string;
  squareFootage: string;
  finishLevel: string;
  firstName: string;
  lastName: string;
  propertyAddress: string;
  email: string;
  phone: string;
}

const ADUCalculatorContent = () => {
  const { theme } = useNeumorphicTheme();
  const [currentStep, setCurrentStep] = useState(1);
  const [showResult, setShowResult] = useState(false);
  const [calculatedCost, setCalculatedCost] = useState(0);
  const [verificationCode, setVerificationCode] = useState("");
  const [sentVerificationCode, setSentVerificationCode] = useState("");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);
  const { toast } = useToast();
  const [formData, setFormData] = useState<FormData>({
    aduType: "",
    county: "",
    city: "",
    purposes: [],
    otherPurpose: "",
    designChoice: "",
    squareFootage: "",
    finishLevel: "",
    firstName: "",
    lastName: "",
    propertyAddress: "",
    email: "",
    phone: ""
  });

  const counties = ["San Mateo County", "Santa Clara County"];
  const cities = {
    "San Mateo County": {
      tier1: ["Atherton", "Belmont", "Hillsborough", "Los Altos", "Los Altos Hills", "Menlo Park", "Palo Alto", "Portola Valley", "Woodside"],
      tier2: ["City of San Mateo", "County of San Mateo", "Redwood City", "San Carlos"]
    },
    "Santa Clara County": {
      tier1: ["Los Gatos", "Monte Sereno"],
      tier2: ["Campbell", "City of Santa Clara", "County of Santa Clara", "Cupertino", "San Jose", "Saratoga", "Sunnyvale", "Mountain View"]
    }
  };
  const aduTypes = ["Detached ADU", "Attached ADU", "Junior ADU (JADU)", "Garage Conversion", "Basement Conversion"];
  const purposeOptions = ["Additional income / rental property", "Housing for elderly parents", "Housing for young adults", "Guest house", "Home office / studio", "Future downsizing / personal use"];
  const designOptions = ["I already have approved plans/permits", "I'm working with an architect", "I'd like a fully custom ADU from UDA", "I'd prefer to choose from UDA's pre-designed plans", "Not sure yet"];
  const finishLevels = [{
    value: "standard",
    label: "Standard",
    cost: 99
  }, {
    value: "elite",
    label: "Elite",
    cost: 198
  }, {
    value: "deluxe",
    label: "Deluxe",
    cost: 297
  }];

  const calculateCost = () => {
    const sf = parseInt(formData.squareFootage);
    let designBuildRate = 0;

    const isTier1 = cities[formData.county as keyof typeof cities]?.tier1.includes(formData.city);

    if (sf < 400) {
      designBuildRate = isTier1 ? 498 : 448;
    } else if (sf <= 800) {
      designBuildRate = isTier1 ? 488 : 438;
    } else {
      designBuildRate = isTier1 ? 478 : 428;
    }

    const finishLevel = finishLevels.find(level => level.value === formData.finishLevel);
    const materialRate = finishLevel?.cost || 99;

    const totalCost = (designBuildRate + materialRate) * sf;
    setCalculatedCost(totalCost);
  };

  const sendVerificationCode = async () => {
    setIsSendingCode(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-verification-sms', {
        body: {
          phoneNumber: formData.phone,
        },
      });

      if (error) throw error;

      if (data.success) {
        setSentVerificationCode(data.verificationCode);
        setIsCodeSent(true);
        toast({
          title: "Verification code sent",
          description: `A 6-digit code has been sent to ${formData.phone}`,
        });
      } else {
        throw new Error(data.error || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      toast({
        title: "Error",
        description: "Failed to send verification code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSendingCode(false);
    }
  };

  const verifyCode = () => {
    if (verificationCode === sentVerificationCode) {
      setIsVerified(true);
      calculateCost();
      setShowResult(true);
      toast({
        title: "Phone verified successfully",
        description: "Your phone number has been verified!",
      });
    } else {
      toast({
        title: "Invalid code",
        description: "The verification code you entered is incorrect. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleNext = () => {
    if (currentStep < 7) {
      setCurrentStep(currentStep + 1);
    } else {
      if (!isCodeSent) {
        sendVerificationCode();
      } else {
        verifyCode();
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 7 && currentStep > 6) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePurposeChange = (purpose: string, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        purposes: [...prev.purposes, purpose]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        purposes: prev.purposes.filter(p => p !== purpose)
      }));
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.aduType !== "";
      case 2:
        return formData.county !== "" && formData.city !== "";
      case 3:
        return formData.purposes.length > 0;
      case 4:
        return formData.designChoice !== "";
      case 5:
        return formData.squareFootage !== "" && formData.finishLevel !== "";
      case 6:
        return formData.firstName !== "" && formData.lastName !== "" && formData.propertyAddress !== "" && formData.email !== "" && formData.phone !== "";
      case 7:
        return !isCodeSent || (isCodeSent && verificationCode.length === 6);
      default:
        return false;
    }
  };

  const openCalendlyModal = () => {
    console.log('Opening Calendly modal');
    setShowCalendly(true);
  };

  const closeCalendlyModal = () => {
    console.log('Closing Calendly modal');
    setShowCalendly(false);
  };

  const getInputStyles = () => {
    return theme === "dark" 
      ? "bg-[#0a0a0a] border-0 text-white shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414] rounded-xl" 
      : "bg-white border-0 text-gray-900 shadow-[6px_6px_12px_#c0c0c0,-6px_-6px_12px_#ffffff] rounded-xl";
  };

  const getOptionStyles = (isSelected: boolean) => {
    if (isSelected) {
      return 'bg-[#e4683b] text-white';
    }
    return theme === "dark" 
      ? 'bg-[#0a0a0a] text-white shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414] hover:shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414]'
      : 'bg-white text-gray-900 shadow-[6px_6px_12px_#c0c0c0,-6px_-6px_12px_#ffffff] hover:shadow-[8px_8px_16px_#c0c0c0,-8px_-8px_16px_#ffffff]';
  };

  const headingClass = theme === "dark" ? "text-white" : "text-gray-900";
  const labelClass = theme === "dark" ? "text-white" : "text-gray-900";

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <div className="space-y-4">
            <h2 className={`text-xl md:text-2xl font-semibold mb-4 ${headingClass}`}>Type of ADU</h2>
            <div className="grid gap-3">
              {aduTypes.map(type => <label key={type} className={`flex items-center space-x-3 p-3 rounded-xl cursor-pointer transition-all duration-200 ${getOptionStyles(formData.aduType === type)}`}>
                  <input type="radio" name="aduType" value={type} checked={formData.aduType === type} onChange={e => setFormData(prev => ({
                ...prev,
                aduType: e.target.value
              }))} className="w-4 h-4 text-[#e4683b] accent-[#e4683b]" />
                  <span className="text-sm md:text-base">{type}</span>
                </label>)}
            </div>
          </div>;

      case 2:
        return <div className="space-y-4">
            <h2 className={`text-xl md:text-2xl font-semibold mb-4 ${headingClass}`}>Select Your County and City</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="county" className={labelClass}>County</Label>
                <Select value={formData.county} onValueChange={value => setFormData(prev => ({
                ...prev,
                county: value,
                city: ""
              }))}>
                  <SelectTrigger className={getInputStyles()}>
                    <SelectValue placeholder="Select County" />
                  </SelectTrigger>
                  <SelectContent className={theme === "dark" ? "bg-[#0a0a0a] border-0 text-white" : "bg-white border-0 text-gray-900"}>
                    {counties.map(county => <SelectItem key={county} value={county}>{county}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              {formData.county && <div>
                  <Label htmlFor="city" className={labelClass}>City</Label>
                  <Select value={formData.city} onValueChange={value => setFormData(prev => ({
                ...prev,
                city: value
              }))}>
                    <SelectTrigger className={getInputStyles()}>
                      <SelectValue placeholder="Select City" />
                    </SelectTrigger>
                    <SelectContent className={theme === "dark" ? "bg-[#0a0a0a] border-0 text-white" : "bg-white border-0 text-gray-900"}>
                      {cities[formData.county as keyof typeof cities]?.tier1.map(city => <SelectItem key={city} value={city}>{city}</SelectItem>)}
                      {cities[formData.county as keyof typeof cities]?.tier2.map(city => <SelectItem key={city} value={city}>{city}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>}
            </div>
          </div>;

      case 3:
        return <div className="space-y-4">
            <h2 className={`text-xl md:text-2xl font-semibold mb-4 ${headingClass}`}>Purpose of ADU</h2>
            <div className="space-y-3">
              {purposeOptions.map(purpose => <div key={purpose} className={`flex items-center space-x-2 p-3 rounded-xl ${getOptionStyles(false)}`}>
                  <Checkbox id={purpose} checked={formData.purposes.includes(purpose)} onCheckedChange={checked => handlePurposeChange(purpose, checked as boolean)} className={theme === "dark" ? "border-gray-600" : "border-gray-400"} />
                  <Label htmlFor={purpose} className={`text-sm md:text-base cursor-pointer ${labelClass}`}>{purpose}</Label>
                </div>)}
              <div className={`flex items-center space-x-2 p-3 rounded-xl ${getOptionStyles(false)}`}>
                <Checkbox id="other" checked={formData.purposes.includes("Other")} onCheckedChange={checked => {
                if (checked) {
                  setFormData(prev => ({
                    ...prev,
                    purposes: [...prev.purposes, "Other"]
                  }));
                } else {
                  setFormData(prev => ({
                    ...prev,
                    purposes: prev.purposes.filter(p => p !== "Other"),
                    otherPurpose: ""
                  }));
                }
              }} className={theme === "dark" ? "border-gray-600" : "border-gray-400"} />
                <Label htmlFor="other" className={`text-sm md:text-base cursor-pointer ${labelClass}`}>Other</Label>
              </div>
              {formData.purposes.includes("Other") && <Input placeholder="Please specify..." value={formData.otherPurpose} onChange={e => setFormData(prev => ({
              ...prev,
              otherPurpose: e.target.value
            }))} className={`mt-2 ${getInputStyles()}`} />}
            </div>
          </div>;

      case 4:
        return <div className="space-y-4">
            <h2 className={`text-xl md:text-2xl font-semibold mb-4 ${headingClass}`}>Design & Permitting</h2>
            <div className="grid gap-3">
              {designOptions.map(option => <label key={option} className={`flex items-center space-x-3 p-3 rounded-xl cursor-pointer transition-all duration-200 ${getOptionStyles(formData.designChoice === option)}`}>
                  <input type="radio" name="designChoice" value={option} checked={formData.designChoice === option} onChange={e => setFormData(prev => ({
                ...prev,
                designChoice: e.target.value
              }))} className="w-4 h-4 text-[#e4683b] accent-[#e4683b]" />
                  <span className="text-sm md:text-base">{option}</span>
                </label>)}
            </div>
          </div>;

      case 5:
        return <div className="space-y-4">
            <h2 className={`text-xl md:text-2xl font-semibold mb-4 ${headingClass}`}>Square Footage and Finish Level</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="squareFootage" className={labelClass}>Square Footage</Label>
                <Input id="squareFootage" type="number" placeholder="Enter square footage" value={formData.squareFootage} onChange={e => setFormData(prev => ({
                ...prev,
                squareFootage: e.target.value
              }))} className={`${getInputStyles()} placeholder:text-gray-500`} />
              </div>
              
              <div>
                <Label htmlFor="finishLevel" className={labelClass}>Finish Material Category</Label>
                <Select value={formData.finishLevel} onValueChange={value => setFormData(prev => ({
                ...prev,
                finishLevel: value
              }))}>
                  <SelectTrigger className={getInputStyles()}>
                    <SelectValue placeholder="Select finish level" />
                  </SelectTrigger>
                  <SelectContent className={theme === "dark" ? "bg-[#0a0a0a] border-0 text-white" : "bg-white border-0 text-gray-900"}>
                    {finishLevels.map(level => <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>;

      case 6:
        return <div className="space-y-4">
            <h2 className={`text-xl md:text-2xl font-semibold mb-4 ${headingClass}`}>Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName" className={labelClass}>First Name *</Label>
                <Input id="firstName" value={formData.firstName} onChange={e => setFormData(prev => ({
                ...prev,
                firstName: e.target.value
              }))} required className={getInputStyles()} />
              </div>
              <div>
                <Label htmlFor="lastName" className={labelClass}>Last Name *</Label>
                <Input id="lastName" value={formData.lastName} onChange={e => setFormData(prev => ({
                ...prev,
                lastName: e.target.value
              }))} required className={getInputStyles()} />
              </div>
            </div>
            <div>
              <Label htmlFor="propertyAddress" className={labelClass}>Property Address *</Label>
              <Input id="propertyAddress" value={formData.propertyAddress} onChange={e => setFormData(prev => ({
              ...prev,
              propertyAddress: e.target.value
            }))} required className={getInputStyles()} />
            </div>
            <div>
              <Label htmlFor="email" className={labelClass}>Email Address *</Label>
              <Input id="email" type="email" value={formData.email} onChange={e => setFormData(prev => ({
              ...prev,
              email: e.target.value
            }))} required className={getInputStyles()} />
            </div>
            <div>
              <Label htmlFor="phone" className={labelClass}>Phone Number *</Label>
              <Input id="phone" type="tel" value={formData.phone} onChange={e => setFormData(prev => ({
              ...prev,
              phone: e.target.value
            }))} required className={getInputStyles()} />
            </div>
          </div>;

      case 7:
        const otpSlotClass = theme === "dark" 
          ? "bg-[#0a0a0a] border-0 text-white shadow-[4px_4px_8px_#000000,-4px_-4px_8px_#141414]"
          : "bg-white border-0 text-gray-900 shadow-[4px_4px_8px_#c0c0c0,-4px_-4px_8px_#ffffff]";
        return <div className="space-y-4">
            <h2 className={`text-xl md:text-2xl font-semibold mb-4 ${headingClass}`}>Phone Verification</h2>
            {!isCodeSent ? (
              <div className="text-center space-y-4">
                <p className={theme === "dark" ? "text-gray-400" : "text-gray-600"}>
                  We'll send a verification code to {formData.phone} to verify your phone number.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className={`text-center ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
                  Enter the 6-digit verification code sent to {formData.phone}
                </p>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={verificationCode}
                    onChange={setVerificationCode}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} className={otpSlotClass} />
                      <InputOTPSlot index={1} className={otpSlotClass} />
                      <InputOTPSlot index={2} className={otpSlotClass} />
                      <InputOTPSlot index={3} className={otpSlotClass} />
                      <InputOTPSlot index={4} className={otpSlotClass} />
                      <InputOTPSlot index={5} className={otpSlotClass} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <div className="text-center">
                  <Button 
                    variant="link" 
                    onClick={() => {
                      setIsCodeSent(false);
                      setVerificationCode("");
                      setSentVerificationCode("");
                    }}
                    disabled={isSendingCode}
                    className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
                  >
                    Didn't receive the code? Send again
                  </Button>
                </div>
              </div>
            )}
          </div>;

      default:
        return null;
    }
  };

  useEffect(() => {
    // Load Calendly popup widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => {
      console.log('Calendly script loaded successfully');
    };
    script.onerror = () => {
      console.error('Failed to load Calendly script');
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup script when component unmounts
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript && document.head.contains(existingScript)) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <NeumorphicContainer>
      <CanonicalTag url="https://www.udaadu.com/adu-calculator" />
      <Navigation />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative w-full max-w-4xl h-[90vh] bg-white rounded-lg overflow-hidden">
            <button 
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a meeting"
            />
          </div>
        </div>
      )}
      
      <section className={`py-12 px-4 min-h-screen ${theme === "dark" ? "bg-[#0a0a0a]" : "bg-[#f5f5f5]"}`}>
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }}>
            <Card className={`border-0 rounded-3xl ${theme === "dark" ? "bg-[#0a0a0a] shadow-[inset_12px_12px_24px_#000000,inset_-12px_-12px_24px_#141414]" : "bg-white shadow-[inset_8px_8px_16px_#c0c0c0,inset_-8px_-8px_16px_#ffffff]"}`}>
              <CardHeader className="text-center">
                <CardTitle className={`tracking-tight flex items-center justify-center gap-2 text-2xl md:text-4xl font-medium ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                  <Calculator className="h-6 w-6 md:h-8 md:w-8 text-[#e4683b]" />
                  UDA ADU Calculator
                </CardTitle>
                <div className="flex justify-center mt-4">
                  <div className="flex space-x-2">
                    {[1, 2, 3, 4, 5, 6, 7].map(step => <div key={step} className={`w-3 h-3 rounded-full ${theme === "dark" ? "shadow-[2px_2px_4px_#000000,-2px_-2px_4px_#141414]" : "shadow-[2px_2px_4px_#c0c0c0,-2px_-2px_4px_#ffffff]"} ${step === currentStep ? "bg-[#e4683b]" : step < currentStep ? "bg-[#e4683b]/60" : theme === "dark" ? "bg-[#1a1a1a]" : "bg-[#e0e0e0]"}`} />)}
                  </div>
                </div>
                <p className={`text-sm mt-2 ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>Step {currentStep} of 7</p>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {renderStep()}
                
                <div className="flex justify-between pt-6">
                  <Button 
                    variant="secondary" 
                    onClick={handleBack} 
                    disabled={currentStep <= 6} 
                    className={`flex items-center gap-2 rounded-xl border-0 ${theme === "dark" ? "bg-[#0a0a0a] text-white hover:bg-[#141414] disabled:bg-[#0a0a0a] disabled:text-gray-600 shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414]" : "bg-white text-gray-900 hover:bg-gray-100 disabled:bg-white disabled:text-gray-400 shadow-[6px_6px_12px_#c0c0c0,-6px_-6px_12px_#ffffff]"}`}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Back
                  </Button>
                  
                  <Button 
                    onClick={handleNext} 
                    disabled={!isStepValid() || isSendingCode} 
                    className={`flex items-center gap-2 rounded-xl border-0 ${theme === "dark" ? "bg-[#0a0a0a] hover:bg-[#141414] text-white shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414]" : "bg-white hover:bg-gray-100 text-gray-900 shadow-[6px_6px_12px_#c0c0c0,-6px_-6px_12px_#ffffff]"}`}
                  >
                    {currentStep === 7 ? (!isCodeSent ? (isSendingCode ? "Sending..." : "Send Verification Code") : "Verify & View Price") : "Next"}
                    {currentStep < 7 && <ChevronRight className="h-4 w-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Result Dialog */}
      <Dialog open={showResult} onOpenChange={setShowResult}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => {
          setShowResult(false);
        }}>
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">Your ADU Cost Estimate</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Calendly Widget */}
            <div className="w-full">
              <h3 className="text-lg font-semibold mb-4 text-center">Schedule Your Free Consultation</h3>
              <div className="w-full h-[500px] border rounded-lg">
                <iframe 
                  src="https://calendly.com/udaadu" 
                  width="100%" 
                  height="100%" 
                  frameBorder="0" 
                  title="Schedule Consultation"
                  className="rounded-lg"
                ></iframe>
              </div>
            </div>
            
            {/* Cost Estimate */}
            <div className="text-center space-y-4">
              <div className="bg-[#e4683b]/10 p-6 rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Your complete design, build, and material cost for the entire project starts at:</p>
                <p className="text-3xl font-bold text-[#e4683b]">
                  ${calculatedCost.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground mt-3 italic">
                  Your actual cost may vary slightly depending on site conditions and scope — every detail matters. We'll provide an exact, itemized quote during your complimentary ADU meeting.
                </p>
              </div>
              
              <div className="text-sm text-muted-foreground p-4 bg-accent rounded-lg">
                <p>We offer special discounts, grant options, and in-house financing to support our local community. By scheduling and showing up to your appointment with our CEO, you'll automatically receive a 5% discount, with additional benefits discussed based on your situation.</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <Footer />
      <FloatingADUWizard />
      <NeumorphicThemeToggle />
    </NeumorphicContainer>
  );
};

const ADUCalculator = () => {
  return (
    <NeumorphicThemeProvider>
      <ADUCalculatorContent />
    </NeumorphicThemeProvider>
  );
};

export default ADUCalculator;
